import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useBrightContext } from "../contexts/brightContext";
import { useViewOptions } from "../contexts/viewOptionContext";
import { useRootSelector } from "../stateSelectors";
import { baseEramHubConnection } from "../../eramHubConnectionFactory";
import { eramTextDimensionMap } from "../../eramFont";
import { EramInput, useInputProps } from "./EramInput";
import { MenuElement } from "./MenuElement";
import { colorNameMap } from "../../colorNameMap";
import { useOnUnmount } from "../hooks/useOnUnmount";
import { updateViewMenuFields, viewMenuFieldsSelector } from "../slices/viewMenuFieldsSlice";
import { useCommonDispatch } from "../../createRootReducer";
const view = "ALTIM_SET";
export const altimSetTemplateViewMenu = "ALTIM_SET_TEMPLATE_MENU";
const { width: fontWidth, height: fontHeight } = eramTextDimensionMap[2];
export const altimViewTemplateColWidth = 5 * fontWidth + 4;
const rowHeight = fontHeight + 3;
const AltimSetTemplateItem = ({ station, x, y, onmousedown, selected, addStation, }) => {
    const { borderTint, tint } = useBrightContext();
    const path = `${view}/altimTemplate/${x}/${y}`;
    const [showInvalid, setShowInvalid] = useState(false);
    const inputProps = useInputProps(path, "", (s) => {
        // TODO: set error based on existence of the station
        if (!/[A-Z]{3}/.test(s)) {
            setShowInvalid(true);
            return;
        }
        setShowInvalid(false);
        addStation(s);
    });
    return !station ? (_jsxs(_Fragment, { children: [_jsx(EramInput, { x: x * altimViewTemplateColWidth, y: y * rowHeight + 1, width: altimViewTemplateColWidth, height: rowHeight, textX: 2, textY: 2, borderTint: borderTint, fill: 0, tint: tint, highlightColor: 0xf0f0f0, ...inputProps, onmousedown: (e) => {
                    onmousedown();
                    void inputProps.onmousedown(e);
                } }), showInvalid && (_jsx(MenuElement, { x: (x + 1) * altimViewTemplateColWidth, y: y * rowHeight + 1, zIndex: 10, text: "INVALID", tint: colorNameMap.yellow, fillColor: 0 }))] })) : (_jsx(MenuElement, { x: x * altimViewTemplateColWidth, y: y * rowHeight, width: altimViewTemplateColWidth, height: rowHeight, borderColor: borderTint, fillColor: 0, tint: tint, textX: 2, textY: 2, text: station, selected: selected, selectReverseVideo: true, onmousedown: onmousedown }));
};
const PositionlessEntryArea = ({ selectedStation, stations, selectStation }) => {
    const { borderTint } = useBrightContext();
    const viewOptions = useViewOptions();
    const width = altimViewTemplateColWidth * viewOptions.column;
    const height = 1 + rowHeight * Math.ceil((stations.length * (fontWidth * 4 + 4)) / width);
    let x = 2;
    let y = 2;
    return (_jsxs("container", { x: 0, y: rowHeight * Math.floor(24 / viewOptions.column), sortableChildren: true, children: [_jsx("graphics", { draw: (g) => {
                    g.rect(0, 0, width, height + 4)
                        .fill(0)
                        .stroke({ width: 1, color: borderTint });
                } }), stations.map((station) => {
                if (x + fontWidth * 4 + 4 > width) {
                    x = 2;
                    y += rowHeight;
                }
                const _x = x;
                x += fontWidth * 4 + 4;
                return (_jsx(MenuElement, { x: _x, y: y, width: fontWidth * 4, height: fontHeight + 1, borderColor: 0, fillColor: 0, tint: colorNameMap.mediumGrey, text: station, selectReverseVideo: true, selected: selectedStation === station, onmousedown: () => selectStation(station) }, station));
            })] }));
};
export const AltimSetTemplate = () => {
    const dispatch = useCommonDispatch();
    const viewOptions = useViewOptions();
    const { selectedStation, draftState, peaStations } = useRootSelector((state) => viewMenuFieldsSelector(state, altimSetTemplateViewMenu));
    useOnUnmount(() => {
        baseEramHubConnection.emit("setAltimStations", draftState);
        dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "open", false));
    });
    return (_jsxs("container", { sortableChildren: true, children: [Array.from({ length: 24 }, (_, index) => {
                const row = Math.floor(index / viewOptions.column);
                const col = index % viewOptions.column;
                const station = draftState.find((v) => v.index === index)?.station;
                return (_jsx(AltimSetTemplateItem, { x: col, y: row, station: station, selected: selectedStation === station, addStation: (station) => {
                        dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "draftState", (prev) => [
                            ...prev,
                            { index, station },
                        ]));
                    }, onmousedown: () => {
                        if (station) {
                            if (selectedStation === station) {
                                dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "selectedStation", null));
                                return;
                            }
                            if (!selectedStation) {
                                dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "selectedStation", station));
                            }
                            else {
                                dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "peaStations", (prev) => prev.includes(station)
                                    ? prev.filter((a) => a !== station && a !== selectedStation)
                                    : [...prev.filter((a) => a !== selectedStation), station]));
                                dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "draftState", (prev) => [
                                    ...prev.filter((v) => ![station, selectedStation].includes(v.station)),
                                    { index, station: selectedStation },
                                ]));
                                dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "selectedStation", null));
                            }
                        }
                        else {
                            if (selectedStation) {
                                dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "draftState", (prev) => [
                                    ...prev.filter((v) => v.station !== selectedStation),
                                    { index, station: selectedStation },
                                ]));
                                dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "peaStations", (prev) => prev.filter((v) => v !== selectedStation)));
                            }
                            dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "selectedStation", null));
                        }
                    } }, `${index}-${station ?? ""}`));
            }), peaStations.length > 0 && (_jsx(PositionlessEntryArea, { selectedStation: selectedStation, stations: peaStations, selectStation: (station) => dispatch(updateViewMenuFields(altimSetTemplateViewMenu, "selectedStation", (prev) => prev === station ? null : station)) }))] }));
};
//# sourceMappingURL=AltimSetTemplate.js.map